/**
 * Carrier cutoff status helper
 * All cutoff times are "HH:mm" strings in Asia/Baghdad (same convention as utils/date.ts),
 * interpreted within the OPERATIONAL day (04:00 -> 04:00 next day): times before 04:00
 * fall at the END of the day - carriers collect overnight (e.g. start 20:30, cutoff 03:30).
 * Pure functions - the caller injects "now" so countdowns are testable and tickable.
 */

const TIMEZONE = 'Asia/Baghdad';

/** Operational day starts 04:00 Baghdad (same constant as pickupSlotStatus.ts) */
const OPERATIONAL_DAY_START_MINUTES = 240;

/**
 * Minutes since the 04:00 operational-day start for an "HH:mm" string.
 * "20:30" -> 990, "03:30" -> 1410 (later the same operational day).
 */
export function hhmmToOperationalMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h * 60 + m - OPERATIONAL_DAY_START_MINUTES + 1440) % 1440;
}

export type CutoffState = 'ok' | 'approaching' | 'passed' | 'unconfigured';

export interface CutoffStatus {
  state: CutoffState;
  label: string;
  minutesLeft: number | null;
}

/** Minutes elapsed since midnight in Baghdad for the given instant */
export function getBaghdadMinutesOfDay(now: Date): number {
  const hhmm = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIMEZONE,
  });
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Status of a daily cutoff relative to "now", both measured within the
 * operational day (04:00 -> 04:00), so an 03:30 cutoff counts down all evening:
 * - > 60 min left -> ok ("2h 15m left")
 * - 0-60 min left -> approaching ("45m left")
 * - past cutoff   -> passed ("Passed 03:15")
 * - null/undefined -> unconfigured
 */
export function getCutoffStatus(
  cutoff: string | null | undefined,
  now: Date = new Date()
): CutoffStatus {
  if (!cutoff) {
    return { state: 'unconfigured', label: 'Not configured', minutesLeft: null };
  }

  const cutoffOp = hhmmToOperationalMinutes(cutoff);
  const nowMinutes = getBaghdadMinutesOfDay(now);
  const nowOp = (nowMinutes - OPERATIONAL_DAY_START_MINUTES + 1440) % 1440;
  const diff = cutoffOp - nowOp;

  if (diff < 0) {
    return { state: 'passed', label: `Passed ${cutoff}`, minutesLeft: 0 };
  }
  if (diff <= 60) {
    return { state: 'approaching', label: `${diff}m left`, minutesLeft: diff };
  }
  const hoursLeft = Math.floor(diff / 60);
  const minsLeft = diff % 60;
  return { state: 'ok', label: `${hoursLeft}h ${minsLeft}m left`, minutesLeft: diff };
}

/** Map a cutoff state to the ui Badge variant */
export function getCutoffBadgeVariant(
  state: CutoffState
): 'success' | 'warning' | 'danger' | 'default' {
  switch (state) {
    case 'ok':
      return 'success';
    case 'approaching':
      return 'warning';
    case 'passed':
      return 'danger';
    default:
      return 'default';
  }
}
